/**
 * IndexAddEngineFields — presentational sub of `IndexAddFormBody` covering
 * the per-engine extras:
 *   - Slice C1: filtered / partial WHERE textarea.
 *   - Slice C2: INCLUDE / covering columns input.
 *   - Slice C3: expression / functional-index entries textarea (one
 *     expression per line; each line ships as `{ kind: 'expression', text }`).
 *
 * Orchestrator owns state; this component renders the input controls and
 * propagates change events through setters.
 *
 * Split from `IndexAddFormBody` per the 200-line file budget (B3 split
 * pattern carried forward as the C-phase form gains engine extras).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice C1, § Slice C2, § Slice C3.
 */
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';

export interface IndexAddEngineFieldsProps {
  readonly testId: string;
  readonly showFilter: boolean;
  readonly filterTooltip: string;
  readonly filter: string;
  readonly setFilter: (s: string) => void;
  readonly showInclude: boolean;
  readonly includeTooltip: string;
  readonly include: string;
  readonly setInclude: (s: string) => void;
  readonly showExpression: boolean;
  readonly expressionTooltip: string;
  readonly expression: string;
  readonly setExpression: (s: string) => void;
}

export function IndexAddEngineFields(p: IndexAddEngineFieldsProps) {
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${p.testId}-filter`} className="text-xs">
          Filter (WHERE clause)
        </Label>
        <textarea
          id={`${p.testId}-filter`}
          data-testid={`${p.testId}-filter`}
          value={p.filter}
          onChange={(e) => p.setFilter(e.target.value)}
          placeholder="status = 'active'"
          disabled={!p.showFilter}
          title={!p.showFilter ? p.filterTooltip : undefined}
          className="min-h-[64px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-60"
        />
        <span className="text-[10px] text-muted-foreground">
          {p.showFilter
            ? 'Optional partial-index predicate (omit the WHERE keyword — the synthesizer adds it).'
            : p.filterTooltip}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${p.testId}-include`} className="text-xs">
          INCLUDE columns
        </Label>
        <Input
          id={`${p.testId}-include`}
          data-testid={`${p.testId}-include`}
          value={p.include}
          onChange={(e) => p.setInclude(e.target.value)}
          placeholder="name, email"
          disabled={!p.showInclude}
          title={!p.showInclude ? p.includeTooltip : undefined}
          className="h-8 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-60"
        />
        <span className="text-[10px] text-muted-foreground">
          {p.showInclude
            ? 'Optional comma-separated covering / non-key columns (mssql 2005+, postgres 11+).'
            : p.includeTooltip}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${p.testId}-expression`} className="text-xs">
          + Expression keys
        </Label>
        <textarea
          id={`${p.testId}-expression`}
          data-testid={`${p.testId}-expression`}
          value={p.expression}
          onChange={(e) => p.setExpression(e.target.value)}
          placeholder={"LOWER(email)\n(json_col->>'key')"}
          disabled={!p.showExpression}
          title={!p.showExpression ? p.expressionTooltip : undefined}
          className="min-h-[64px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs disabled:cursor-not-allowed disabled:opacity-60"
        />
        <span className="text-[10px] text-muted-foreground">
          {p.showExpression
            ? 'Optional — one expression per line. Appended after the comma-separated column list (e.g. LOWER(email)).'
            : p.expressionTooltip}
        </span>
      </div>
    </>
  );
}
